// src/components/idcard/format.ts — ID Card number + date helpers.
//
// Pure functions shared by the card, the QR renderer and the page. Ported
// from the prototype's idcard-data.jsx so the formatting rules live in one
// place: the card number is normalised to a bare 9-digit block for the QR,
// grouped 3-3-3 for display, and dates render DD/MM/YYYY (UK, like the PVC
// card). Robust to however the value arrives (hyphens, spaces, bare digits).

/** Strip non-digits → one scannable block. "372-480-505" → "372480505". */
export const cardDigits = (v: string | null | undefined): string =>
  String(v ?? "").replace(/\D/g, "");

/** True when the value is a clean 9-digit card number. */
export const hasCardNo = (v: string | null | undefined): boolean =>
  cardDigits(v).length === 9;

/** Pretty 3-3-3 grouping for display; falls back to "—" when incomplete. */
export const groupCardNo = (v: string | null | undefined): string => {
  const d = cardDigits(v);
  if (d.length !== 9) return v ? String(v) : "—";
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 9)}`;
};

/** DD/MM/YYYY from an ISO date or timestamp; "—" when absent/unparseable. */
export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

/** The NYMR card never expires. */
export const expiryLabel = (expiry: string | null | undefined): string =>
  expiry ? fmtDate(expiry) : "None — Valid Until Withdrawn";
